import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { gossipEvents } from "@/mocks/events";
import SectionHeading from "@/components/feature/SectionHeading";
import Reveal from "@/components/base/Reveal";
import { formatHeat, formatDate } from "@/lib/format";

const tabs = ["全部", "持续发酵", "进行中", "已澄清"] as const;

export default function HotRadar() {
  const [tab, setTab] = useState<(typeof tabs)[number]>("全部");

  const ranked = useMemo(() => {
    const sorted = [...gossipEvents].sort((a, b) => b.heat - a.heat);
    if (tab === "全部") return sorted.slice(0, 8);
    return sorted.filter((item) => item.status === tab).slice(0, 8);
  }, [tab]);

  const featured = useMemo(() => [...gossipEvents].sort((a, b) => b.heat - a.heat)[0], []);

  return (
    <section id="hot-radar" className="w-full bg-background-100 py-16 md:py-20 scroll-mt-20">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="HOT RADAR"
          title="热瓜雷达 · 今天大家都在追什么"
          desc="按站内热度排序，把正在持续发酵的事件排在最前面；想找已经尘埃落定的旧瓜，可以切换状态筛选。"
          linkTo="/events?sort=heat"
          linkLabel="查看完整热榜"
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1 w-fit">
                {tabs.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setTab(item)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      tab === item
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>

              <ul className="mt-5 flex flex-col gap-2.5">
                {ranked.map((event, index) => (
                  <li key={event.id}>
                    <Link
                      to={`/events/${event.id}`}
                      className="group flex items-center gap-4 rounded-lg px-3 py-3 transition-colors hover:bg-primary-50/60"
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-base font-bold ${
                          index < 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-700 md:text-base">
                          {event.title}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                          <span className="rounded-full bg-accent-100 px-2 py-0.5 text-accent-800">{event.category}</span>
                          <span>{formatDate(event.updatedAt)} 整理</span>
                        </span>
                      </span>
                      <span className="hidden shrink-0 items-center gap-1 text-sm font-semibold text-primary-600 sm:flex">
                        <i className="ri-fire-line" />
                        {formatHeat(event.heat)}
                      </span>
                    </Link>
                  </li>
                ))}
                {ranked.length === 0 && (
                  <li className="rounded-lg bg-background-100 px-4 py-10 text-center text-sm text-foreground-500">
                    这个状态下暂时没有事件，换个筛选看看。
                  </li>
                )}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-5">
            <div className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="relative h-52 w-full overflow-hidden">
                <img
                  src={featured.cover}
                  alt={`${featured.title}事件脉络配图`}
                  title={`${featured.title} - 网报吃瓜事件库`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  本周最热
                </span>
                <h3 className="absolute bottom-4 left-4 right-4 font-heading text-lg font-bold leading-snug text-white">
                  {featured.title}
                </h3>
              </div>

              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-3 text-xs text-foreground-500">
                  <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-accent-800">{featured.category}</span>
                  <span className="flex items-center gap-1 font-semibold text-primary-600">
                    <i className="ri-fire-line" />
                    {formatHeat(featured.heat)}
                  </span>
                  <span>{featured.duration}内整理</span>
                </div>

                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{featured.summary}</p>

                <ul className="mt-4 flex flex-col gap-2 border-t border-background-200 pt-4">
                  {featured.timeline.slice(0, 3).map((step) => (
                    <li key={step.time} className="flex items-start gap-3 text-xs text-foreground-600">
                      <span className="mt-1 flex h-1.5 w-1.5 shrink-0 rounded-full bg-primary-400" />
                      <span className="flex-1">
                        <strong className="font-semibold text-foreground-800">{step.title}</strong>
                        <span className="ml-1 text-foreground-500">{step.time}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/events/${featured.id}`}
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800 whitespace-nowrap"
                >
                  从头看这条事件串
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}